import { getPolls } from "@/lib/server-functions/get-polls";
import { PollWithOptions } from "@/lib/server-functions/helper-functions/fetch-polls";
import { QUERY_KEYS } from "@/lib/tanstack-query/queryKeys";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";

interface UsePollsOptions {
  authorUsername?: string;
  sort?: "best" | "new";
  onlyFollowing?: boolean;
  nsfw?: boolean;
}

export const usePolls = ({
  authorUsername,
  sort = "new",
  onlyFollowing,
  nsfw,
}: UsePollsOptions = {}) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery<PollWithOptions[]>({
    queryKey: authorUsername
      ? [QUERY_KEYS.POLLS, authorUsername, sort, nsfw ? "nsfw" : "sfw"]
      : [
          QUERY_KEYS.POLLS,
          sort,
          onlyFollowing ? "following" : "for-you",
          nsfw ? "nsfw" : "sfw",
        ],
    queryFn: async ({ pageParam }) => {
      const polls = await getPolls({
        data: {
          pageParam: pageParam as string | undefined,
          authorUsername,
          sort,
          onlyFollowing,
          nsfw,
        },
      });

      for (const poll of polls) {
        const existing = queryClient.getQueryData([
          QUERY_KEYS.POLL,
          poll.shortId,
          poll.slug,
        ]);
        if (!existing) {
          queryClient.setQueryData(
            [QUERY_KEYS.POLL, poll.shortId, poll.slug],
            poll
          );
        }
      }

      return polls;
    },
    getNextPageParam: (lastPage) => {
      if (
        !lastPage ||
        lastPage.length < Number(import.meta.env.VITE_PAGE_SIZE)
      ) {
        return undefined;
      }

      const last = lastPage[lastPage.length - 1];

      return JSON.stringify({
        lastScore: last.reactionScore,
        lastId: last.id,
      });
    },
    select: (data) => {
      return {
        ...data,
        pages: data.pages.map((page) =>
          page.map(
            (poll) =>
              queryClient.getQueryData([
                QUERY_KEYS.POLL,
                poll.shortId,
                poll.slug,
              ]) ?? poll
          )
        ),
      };
    },
    initialPageParam: undefined,
    staleTime: Infinity,
  });
};
